import { useEffect, useMemo, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { DailyPollType } from "../../services/dailyPoll.service";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

const defaultOption = {
  text: "",
  colorHex: "",
};

const defaultValues = {
  question: "",
  type: DailyPollType.Binary,
  publishAt: "",
  expiresAt: "",
  options: [defaultOption, defaultOption],
};

const colorHexRegex = /^#[0-9A-Fa-f]{6}$/;

const toDateTimeInputValue = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "";
  }
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const normalizeOptions = (options) => {
  const mapped = (options || []).map((option) => ({
    text: option?.text || "",
    colorHex: option?.colorHex || "",
  }));
  const trimmed = mapped.slice(0, MAX_OPTIONS);
  const next = [...trimmed];
  while (next.length < MIN_OPTIONS) {
    next.push({
      text: "",
      colorHex: "",
    });
  }
  return next;
};

export const buildDailyPollInitialValues = (poll) => ({
  question: poll?.question || defaultValues.question,
  type: poll?.type || defaultValues.type,
  publishAt: toDateTimeInputValue(poll?.publishAt) || "",
  expiresAt: toDateTimeInputValue(poll?.expiresAt) || "",
  options: normalizeOptions(poll?.options),
});

const DailyPollForm = ({
  title,
  initialValues,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  onSubmit,
  onCancel,
  isSubmitting = false,
  optionsLocked = false,
}) => {
  const resolvedInitial = useMemo(
    () => buildDailyPollInitialValues(initialValues),
    [initialValues],
  );
  const [values, setValues] = useState(resolvedInitial);
  const [errors, setErrors] = useState({});

  const optionsCountError = useMemo(() => {
    if (values.type !== DailyPollType.Binary) {
      if (values.options.length < MIN_OPTIONS) {
        return `Options must be at least ${MIN_OPTIONS}`;
      }
      if (values.options.length > MAX_OPTIONS) {
        return `Options cannot exceed ${MAX_OPTIONS}`;
      }
      return "";
    }
    return values.options.length !== MIN_OPTIONS
      ? "Binary polls require exactly 2 options."
      : "";
  }, [values]);

  const canEditOptions = useMemo(
    () => !isSubmitting && !optionsLocked,
    [isSubmitting, optionsLocked],
  );
  const canAddOption = useMemo(
    () =>
      canEditOptions &&
      values.type !== DailyPollType.Binary &&
      values.options.length < MAX_OPTIONS,
    [canEditOptions, values.type, values.options.length],
  );
  const canRemoveOptions = useMemo(
    () =>
      canEditOptions &&
      values.type !== DailyPollType.Binary &&
      values.options.length > MIN_OPTIONS,
    [canEditOptions, values.type, values.options.length],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleOptionChange = (index, field, value) => {
    setValues((prev) => {
      const nextOptions = [...prev.options];
      nextOptions[index] = {
        ...nextOptions[index],
        [field]: value,
      };
      return { ...prev, options: nextOptions };
    });
    setErrors((prev) => ({
      ...prev,
      [`options.${index}.${field}`]: "",
    }));
  };

  const handleAddOption = () => {
    if (values.type === DailyPollType.Binary) {
      return;
    }
    if (values.options.length >= MAX_OPTIONS) {
      return;
    }
    setValues((prev) => ({
      ...prev,
      options: [...prev.options, defaultOption],
    }));
    setErrors((prev) => ({ ...prev, options: "" }));
  };

  const handleRemoveOption = (index) => {
    if (values.type === DailyPollType.Binary) {
      return;
    }
    if (values.options.length <= MIN_OPTIONS) {
      return;
    }
    setValues((prev) => ({
      ...prev,
      options: prev.options.filter((_, optionIndex) => optionIndex !== index),
    }));
    setErrors((prev) => ({ ...prev, options: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedQuestion = values.question.trim();

    if (!trimmedQuestion) {
      newErrors.question = "Question is required";
    } else if (trimmedQuestion.length > 500) {
      newErrors.question = "Question must be at most 500 characters";
    }

    if (!Object.values(DailyPollType).includes(values.type)) {
      newErrors.type = "Type is required";
    }

    if (values.type === DailyPollType.Binary) {
      if (values.options.length !== MIN_OPTIONS) {
        newErrors.options = "Binary polls must have exactly 2 options";
      }
    } else if (
      values.options.length < MIN_OPTIONS ||
      values.options.length > MAX_OPTIONS
    ) {
      newErrors.options = `Options must be between ${MIN_OPTIONS} and ${MAX_OPTIONS}`;
    }

    values.options.forEach((option, index) => {
      const trimmedText = option.text.trim();
      const trimmedColor = option.colorHex.trim();
      if (!trimmedText) {
        newErrors[`options.${index}.text`] = "Option text is required";
      } else if (trimmedText.length > 255) {
        newErrors[`options.${index}.text`] =
          "Option text must be at most 255 characters";
      }

      if (!trimmedColor) {
        newErrors[`options.${index}.colorHex`] = "Color is required";
      } else if (trimmedColor.length > 20) {
        newErrors[`options.${index}.colorHex`] =
          "Color must be at most 20 characters";
      } else if (!colorHexRegex.test(trimmedColor)) {
        newErrors[`options.${index}.colorHex`] = "Color must be #RRGGBB format";
      }
    });

    if (values.publishAt && values.expiresAt) {
      const publishDate = new Date(values.publishAt);
      const expiresDate = new Date(values.expiresAt);
      if (isNaN(publishDate.getTime())) {
        newErrors.publishAt = "Invalid publish date";
      }
      if (isNaN(expiresDate.getTime())) {
        newErrors.expiresAt = "Invalid expires date";
      }
      if (
        !isNaN(publishDate.getTime()) &&
        !isNaN(expiresDate.getTime()) &&
        publishDate.getTime() >= expiresDate.getTime()
      ) {
        newErrors.expiresAt = "Expires At must be after Publish At";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      question: values.question.trim(),
      type: values.type,
      options: values.options.map((option) => {
        const optionData = {
          text: option.text.trim(),
          colorHex: option.colorHex.trim(),
        };
        if (option.id) {
          optionData.id = option.id;
        }
        return optionData;
      }),
    };

    if (values.publishAt) {
      const publishDate = new Date(values.publishAt);
      if (!isNaN(publishDate.getTime())) {
        payload.publishAt = publishDate.toISOString();
      }
    }
    if (values.expiresAt) {
      const expiresDate = new Date(values.expiresAt);
      if (!isNaN(expiresDate.getTime())) {
        payload.expiresAt = expiresDate.toISOString();
      }
    }

    onSubmit?.(payload);
  };

  const handleCancel = () => {
    setValues(resolvedInitial);
    setErrors({});
    onCancel?.();
  };

  useEffect(() => {
    setValues(resolvedInitial);
    setErrors({});
  }, [resolvedInitial]);

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {title && (
        <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
          {title}
        </Typography>
      )}
      <TextField
        margin="normal"
        fullWidth
        required
        id="question"
        name="question"
        label="Question"
        value={values.question}
        onChange={handleChange}
        disabled={isSubmitting}
        error={!!errors.question}
        helperText={errors.question}
        inputProps={{ maxLength: 500 }}
      />

      {/* TODO: Only binary choice is supported for now */}
      {/* <FormControl fullWidth margin="normal" error={!!errors.type}>
        <InputLabel id="type-label">Type</InputLabel>
        <Select
          labelId="type-label"
          id="type"
          name="type"
          value={values.type}
          onChange={handleChange}
          disabled={isSubmitting}
          label="Type"
        >
          <MenuItem value={DailyPollType.Binary}>Binary</MenuItem>
          <MenuItem value={DailyPollType.Multiple}>
            Multiple Choice
          </MenuItem>
        </Select>
        {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
      </FormControl> */}

      <TextField
        margin="normal"
        fullWidth
        id="publishAt"
        name="publishAt"
        label="Publish At"
        type="datetime-local"
        value={values.publishAt}
        onChange={handleChange}
        disabled={isSubmitting}
        error={!!errors.publishAt}
        helperText={errors.publishAt}
        InputLabelProps={{
          shrink: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        id="expiresAt"
        name="expiresAt"
        label="Expires At"
        type="datetime-local"
        value={values.expiresAt}
        onChange={handleChange}
        disabled={isSubmitting}
        error={!!errors.expiresAt}
        helperText={errors.expiresAt}
        InputLabelProps={{
          shrink: true,
        }}
      />

      <Box sx={{ mt: 3 }}>
        <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
          Options
        </Typography>
        {optionsCountError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {optionsCountError}
          </Typography>
        )}
        {optionsLocked && (
          <Typography color="textSecondary" variant="body2" sx={{ mb: 1 }}>
            Options are locked after votes have been cast.
          </Typography>
        )}
        {values.options.map((option, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              required
              label={`Option ${index + 1} Text`}
              value={option.text}
              onChange={(event) =>
                handleOptionChange(index, "text", event.target.value)
              }
              disabled={!canEditOptions}
              error={!!errors[`options.${index}.text`]}
              helperText={errors[`options.${index}.text`]}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              margin="normal"
              fullWidth
              required
              label={`Option ${index + 1} Color`}
              placeholder="#RRGGBB"
              value={option.colorHex}
              onChange={(event) =>
                handleOptionChange(index, "colorHex", event.target.value)
              }
              disabled={!canEditOptions}
              error={!!errors[`options.${index}.colorHex`]}
              helperText={errors[`options.${index}.colorHex`]}
              inputProps={{ maxLength: 20 }}
            />
            {values.type !== DailyPollType.Binary && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  disabled={!canRemoveOptions}
                  onClick={() => handleRemoveOption(index)}
                >
                  Remove option
                </Button>
              </Box>
            )}
          </Box>
        ))}
        {values.type !== DailyPollType.Binary && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              disabled={!canAddOption}
              onClick={handleAddOption}
            >
              Add option
            </Button>
            <Typography variant="caption" color="textSecondary">
              {values.options.length}/{MAX_OPTIONS} options
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {submitLabel}
        </Button>
        <Button
          variant="outlined"
          disabled={isSubmitting}
          onClick={handleCancel}
        >
          {cancelLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default DailyPollForm;
